/**
 * sale-invoice-columns.tsx — wspólna konfiguracja KOLUMN listy faktur sprzedaży.
 *
 * Jedno źródło prawdy dla dwóch ekranów, które pokazują faktury sprzedaży jako tabelę z kolumnami
 * „jak na liście": strony `SaleInvoices` oraz sekcji kokpitu decyzji (`pages/processes/
 * decision-sections`, „Grupuj według: Kartoteka"). Zmiana tutaj jest widoczna na obu — kolumny
 * nie mogą się rozjechać. Klon `purchase-invoice-columns.tsx` dla kierunku sprzedaży.
 *
 * Zawartość: rejestr nadpisań renderu kolumn rdzennych (`SALE_INVOICE_OVERRIDES`), slugi
 * obsługiwane osobno (`SI_EXCLUDE_SLUGS`), remap sortu (`SI_SORT_SLUG_OVERRIDES`), slugi dołączane
 * do `?columns=` (`SI_ALWAYS_SLUGS`, `SI_COLUMN_COMPANIONS`), mapa id kolumny → slug backendu
 * (`SI_COLUMN_ID_TO_SLUG`), prefiks kluczy i18n kolumn procesu (`SI_PROCESS_LABEL_PREFIX`) i typ
 * wiersza listy (`SaleInvoiceRow`). Kolumny rdzenne/dodatkowe buduje katalog pól
 * (`buildCoreColumns`/`buildExtColumns` z `ext-list-columns`); kolumny procesu — wspólne
 * `process-columns.tsx`; tu jest tylko to, co jest specyficzne dla faktur sprzedaży.
 */

import { SaleSourceBadge } from '@/components/sale-invoices/SaleSourceBadge';
import {
  INVOICE_STATUS_CODES,
  invoiceExportTextOverrides,
  invoiceKindFilterOptions,
  invoiceKindLabel,
  invoiceSourceFilterOptions,
  invoiceStatusLabel,
} from '@/lib/documents/invoice-select-labels';
import { PROCESS_COLUMN_ID_TO_SLUG } from '@/lib/documents/process-columns';
import {
  buildStaticSelectOverride,
  type ColumnCompanionRule,
  type CoreColumnOverride,
  type CoreColumnTranslateFn,
  isFieldFilterable,
} from '@/lib/ext-list-columns';

/** Prefiks kluczy i18n kolumn procesu faktur sprzedaży (patrz `buildProcessColumns`). */
export const SI_PROCESS_LABEL_PREFIX = 'tenant.saleInvoices';

/** Prefiks kluczy i18n etykiet rodzaju faktury i źródła na liście faktur sprzedaży. */
const SI_LABEL_PREFIX = 'tenant.saleInvoices';

/**
 * Slugi rdzenne obsługiwane OSOBNO (dedykowane buildery z filtrami record-select), więc pomijane
 * przez `buildCoreColumns` — inaczej powstałyby dwie kolumny na to samo pole.
 */
export const SI_EXCLUDE_SLUGS: ReadonlySet<string> = new Set(['company_id', 'owner_user_id']);

/**
 * Nadpisania slug→slug-sortu dla `?sort=` (reszta: id kolumny = slug, fallback w
 * `useServerListControls`). „Firma" (`buildCompanyColumn`, id `company`) sortu nie ma (nieobecna).
 * „Nabywca" (id `vendor_id`, referencja) sortuje się po buforowanej NAZWIE (`vendor_name_cached`),
 * nie po uuid — dlatego remap; `isCoreColumnSortable('vendor_id')` też przez to sprawdza nazwę.
 */
export const SI_SORT_SLUG_OVERRIDES: Readonly<Record<string, string>> = {
  vendor_id: 'vendor_name_cached',
};

/**
 * Slugi dołączane do `?columns=` ZAWSZE, niezależnie od widocznych kolumn: `gcrecord` (akcja wiersza
 * restore/delete) i `company_id` (firma wiersza dla akcji zbiorczych — dekretacja: bramka „jedna
 * firma” + reguły firmy; kolumny „Firma” zwykle nie ma na liście, a projekcja zwraca tylko żądane
 * pola, więc bez tego modal „Dekretuj” dostawał pustą listę firm).
 */
export const SI_ALWAYS_SLUGS: readonly string[] = ['gcrecord', 'company_id'];

/**
 * Reguły towarzysza dla `?columns=`: „Nabywca" (composite) czyta nazwę i NIP; kwoty
 * (brutto/netto/VAT) czytają walutę NIEZALEŻNIE od tego, która z trzech jest widoczna — inaczej
 * kolumna „Kwota netto" widoczna bez „Kwota brutto" pokazywałaby cichy fallback 'PLN' na fakturze
 * walutowej.
 */
export const SI_COLUMN_COMPANIONS: readonly ColumnCompanionRule[] = [
  { whenSlug: 'vendor_id', addSlug: 'vendor_name_cached' },
  { whenSlug: 'vendor_id', addSlug: 'vendor_tax_id_cached' },
  { whenSlug: 'amount_gross', addSlug: 'currency_code' },
  { whenSlug: 'amount_net', addSlug: 'currency_code' },
  { whenSlug: 'amount_vat', addSlug: 'currency_code' },
];

/**
 * Mapa id kolumny → slug backendu dla `?columns=` — część STAŁA: kolumny spoza `buildCoreColumns`
 * (firma ma id `company`, kategoria `category`, opiekun, kolumny procesu). Kolumny z katalogu
 * (`buildCoreColumns`) dopisuje wołający pętlą `id = slug`.
 */
export const SI_COLUMN_ID_TO_SLUG: Readonly<Record<string, string>> = {
  company: 'company_id',
  category: 'category',
  owner_user_id: 'owner_user_id',
  ...PROCESS_COLUMN_ID_TO_SLUG,
};

function formatAmount(amount: string | null | undefined, currency: string): string {
  if (amount == null) return '—';
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(n);
}

/**
 * Rejestr nadpisań kolumn rdzennych faktur sprzedaży (per-kartoteka) — render bogaty tam, gdzie
 * generyk (typ→komórka) nie wystarcza. Standalone (poza komponentem): sortowalność bierze z
 * `ctx.resolveSortable(ctx.field)` (ta sama serwerowa reguła co generyk, z remapem sortu przez
 * `SI_SORT_SLUG_OVERRIDES`), więc nadpisania nie muszą domykać `controls`/`sortableSlugs`.
 * Klucze = slugi pól z katalogu (`system_module_fields.slug`).
 */
export const SALE_INVOICE_OVERRIDES: Readonly<Record<string, CoreColumnOverride<SaleInvoiceRow>>> =
  {
    // Status — nazwy jak na karcie faktury (+ `booked`, `new`, `verified` z KSeF), NIE nazwy ze słownika
    // pola (patrz `INVOICE_STATUS_CODES`); nadpisanie omija ścieżkę słownikową generyka.
    status: buildStaticSelectOverride<SaleInvoiceRow>({
      codes: INVOICE_STATUS_CODES,
      labelOf: invoiceStatusLabel,
    }),
    system_number: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 160,
      sortable: resolveSortable?.(field) ?? false,
      headerFilter: { category: 'text', fieldSlug: 'system_number' },
      renderCell: (value) => (
        <span className="font-mono text-xs text-text-secondary">{value ? String(value) : '—'}</span>
      ),
    }),
    invoice_number: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 160,
      hideable: false,
      sortable: resolveSortable?.(field) ?? false,
      headerFilter: { category: 'text', fieldSlug: 'invoice_number' },
      renderCell: (value) => <span className="font-medium text-text-primary">{String(value)}</span>,
    }),
    // „Nabywca" = pole `vendor_id` (po stronie sprzedaży niesie NABYWCĘ, katalog go nazywa
    // „Nabywca" — sam slug jest wspólny z zakupem, referencja do kontrahenta, bez tłumacza
    // etykiety) — renderujemy buforowaną NAZWĘ + NIP (composite, dane z `vendor_name_cached`/
    // `vendor_tax_id_cached`, dołączane do projekcji regułą towarzysza `SI_COLUMN_COMPANIONS`).
    // Filtr = alias similarity `vendor_name` (nie `vendor_id`).
    vendor_id: ({ field, resolveSortable }) => ({
      id: field.slug,
      accessor: 'vendorNameCached',
      header: field.name,
      defaultVisible: true,
      defaultWidth: 240,
      sortable: resolveSortable?.(field) ?? false,
      headerFilter: { category: 'text', fieldSlug: 'vendor_name' },
      renderCell: (_value, row) => (
        <div className="text-text-primary">
          {row.vendorNameCached ?? '—'}
          {row.vendorTaxIdCached ? (
            <span className="ml-2 text-xs text-text-muted">NIP {row.vendorTaxIdCached}</span>
          ) : null}
        </div>
      ),
    }),
    // Typ dokumentu (FA(3): VAT/KOR/…) — raw kod z backendu mapowany na etykietę i18n (jak
    // Contractors objectType / Users userType), nie przez słownik katalogu. Lista wyboru w wierszu
    // filtrów (wartość = kod z bazy) i komórka biorą etykietę z TEJ SAMEJ funkcji (`invoiceKindLabel`);
    // bez `options` wiersz filtrów spadał na martwe pole „Szukaj…" zamiast listy wyboru.
    // `transformLabel` zostaje dla popupu nagłówka i dla torów Kanbana z wartościami spoza `options`
    // (`KanbanBoard` etykietuje nim taki tor). Filtr tylko wtedy, gdy serwer deklaruje pole
    // (`isFieldFilterable`); `filterable: false` — ta kolumna ma listę wyboru, nigdy pole „Szukaj…".
    document_type: ({ field, accessor, t, resolveSortable, resolveFilterable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 150,
      sortable: resolveSortable?.(field) ?? false,
      filterable: false,
      ...(isFieldFilterable(field, resolveFilterable)
        ? {
            headerFilter: {
              category: 'select' as const,
              fieldSlug: 'document_type',
              options: invoiceKindFilterOptions(t, SI_LABEL_PREFIX),
              kanbanFallback: false as const,
              transformLabel: (value: string | null) =>
                value ? invoiceKindLabel(value, t, SI_LABEL_PREFIX) : t('common.lists.emptyValue'),
            },
          }
        : {}),
      renderCell: (value) => (
        <span className="text-text-secondary">
          {value ? invoiceKindLabel(String(value), t, SI_LABEL_PREFIX) : '—'}
        </span>
      ),
    }),
    // Kwota brutto + waluta w jednej komórce (companion `currency_code` dołączany do projekcji
    // regułą towarzysza `SI_COLUMN_COMPANIONS`). `headerFilter` category `number` — BEZ tego
    // nadpisanie gubiło filtr liczbowy: kolumna spadała na legacy „Szukaj…" (input bez fieldSlug —
    // wpisana wartość nigdzie nie leci) zamiast operatorów >,<,>=,<=,= (silnik-list-filtr-nagłówka).
    amount_gross: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 140,
      sortable: resolveSortable?.(field) ?? false,
      dataType: 'number',
      headerFilter: { category: 'number', fieldSlug: 'amount_gross' },
      renderCell: (_value, row) => (
        <span className="tabular-nums text-text-primary">
          {formatAmount(row.amountGross, row.currencyCode ?? 'PLN')}
        </span>
      ),
    }),
    // Kwota netto + waluta — mirror `amount_gross` (poszerzenie listy, silnik-list-filtr-nagłówka).
    // Companion `currency_code` dołączany do projekcji jak przy `amount_gross`.
    amount_net: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 140,
      sortable: resolveSortable?.(field) ?? false,
      dataType: 'number',
      headerFilter: { category: 'number', fieldSlug: 'amount_net' },
      renderCell: (_value, row) => (
        <span className="tabular-nums text-text-primary">
          {formatAmount(row.amountNet, row.currencyCode ?? 'PLN')}
        </span>
      ),
    }),
    // Kwota VAT + waluta — mirror `amount_gross`/`amount_net`.
    amount_vat: ({ field, accessor, resolveSortable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 140,
      sortable: resolveSortable?.(field) ?? false,
      dataType: 'number',
      headerFilter: { category: 'number', fieldSlug: 'amount_vat' },
      renderCell: (_value, row) => (
        <span className="tabular-nums text-text-primary">
          {formatAmount(row.amountVat, row.currencyCode ?? 'PLN')}
        </span>
      ),
    }),
    // Źródło (manual/symfonia/ksef) — plakietka `SaleSourceBadge` (inny słownik niż wolnotekstowe
    // `documents.source` klas STANDARD). Opcje listy wyboru w wierszu filtrów = te same trzy kody z
    // tymi samymi kluczami i18n, które czyta plakietka (`tenant.saleInvoices.source.*`).
    source: ({ field, t, resolveSortable, resolveFilterable }) => ({
      id: field.slug,
      accessor: 'source',
      header: field.name,
      defaultVisible: true,
      defaultWidth: 120,
      sortable: resolveSortable?.(field) ?? false,
      filterable: false,
      ...(isFieldFilterable(field, resolveFilterable)
        ? {
            headerFilter: {
              category: 'select' as const,
              fieldSlug: 'source',
              options: invoiceSourceFilterOptions(t, SI_LABEL_PREFIX),
              kanbanFallback: false as const,
            },
          }
        : {}),
      renderCell: (_value, row) => <SaleSourceBadge source={row.source ?? null} />,
    }),
  };

/**
 * `id` (klucz React), `gcrecord` (akcja wiersza restore/delete), `companyId` (akcje zbiorcze —
 * slugi `gcrecord`/`company_id` są zawsze w `alwaysSlugs`) oraz `invoiceNumber` (kolumna
 * `hideable: false`) są jedynymi polami GWARANTOWANYMI w każdej odpowiedzi —
 * reszta zależy od aktualnie widocznych kolumn (projekcja `?columns=` zwraca tylko zażądane pola).
 * Typ wiersza listy faktur sprzedaży; wiersz sekcji kokpitu decyzji = ten wiersz + kroki decyzji.
 */
export interface SaleInvoiceRow {
  id: string;
  companyId?: string;
  vendorId?: string;
  systemNumber?: string | null;
  invoiceNumber: string;
  ksefNumber?: string | null;
  documentType?: string;
  status?: string;
  urgency?: string;
  issueDate?: string;
  paymentDueDate?: string;
  vendorTaxIdCached?: string | null;
  vendorNameCached?: string | null;
  /** Nazwa kategorii z `record_categories` (LEFT JOIN w deskryptorze) — kolumna „Kategoria". */
  categoryName?: string | null;
  currencyCode?: string;
  amountNet?: string;
  amountVat?: string;
  amountGross?: string;
  source?: string;
  externalId?: string | null;
  externalSource?: string | null;
  ownerUserId?: string | null;
  gcrecord: string | null;
  /**
   * Kolumny przekrojowe silnika list (`apps/api/lib/list-engine/providers/process-columns.ts`) —
   * DOKŁADANE na każdym wierszu gdy tenant ma aktywny moduł `processes`, POMIJANE całkowicie (klucz
   * nieobecny) gdy moduł nieaktywny. `currentStage` = bieżące etapy sprawy w toku (0..N, gałęzie
   * równoległe) albo nazwa etapu końcowego sprawy zakończonej (jeden element).
   */
  activeProcessName?: string | null;
  currentStage?: string[];
  /**
   * Wartości pól dodatkowych (ext) — obecne TYLKO gdy backend odpowiada w trybie projekcji
   * `?columns=` i zażądano ≥1 aktywnego pola ext.
   */
  ext?: Record<string, unknown>;
}

/** Tekst komórek z ozdobnikami do pliku CSV (eksport widoku listy) — patrz `invoiceExportTextOverrides`. */
export function saleInvoiceExportTextOverrides(t: CoreColumnTranslateFn) {
  return invoiceExportTextOverrides<SaleInvoiceRow>(t, SI_LABEL_PREFIX);
}
